
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@/components/ui/form';
import { saasSignupSchema, SaasSignupFormValues } from '@/schemas/saasSignupSchema';
import PersonalInfoFields from './PersonalInfoFields';
import BusinessInfoFields from './BusinessInfoFields';
import PasswordFields from './PasswordFields';
import SimpleCaptcha from '@/components/auth/SimpleCaptcha';
import SignupFormFooter from './SignupFormFooter';
import AuthFooter from '@/components/auth/AuthFooter';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

interface SaasSignupFormContainerProps {
  onSubmit: (data: SaasSignupFormValues & { captcha: string }) => void;
  isLoading: boolean;
}

const SaasSignupFormContainer = ({ onSubmit, isLoading }: SaasSignupFormContainerProps) => {
  const [captchaText, setCaptchaText] = useState('');
  
  const form = useForm<SaasSignupFormValues & { captcha: string }>({
    resolver: zodResolver(saasSignupSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      address: '',
      country: '',
      phone: '',
      password: '',
      confirmPassword: '',
      terms: false,
      captcha: '',
    },
  });
  
  const handleSubmit = (data: SaasSignupFormValues & { captcha: string }) => {
    if (data.captcha !== captchaText) {
      form.setError("captcha", { 
        type: "manual", 
        message: "CAPTCHA verification failed. Please try again." 
      });
      return;
    }
    
    onSubmit(data);
  };
  
  const handleCaptchaGenerate = (text: string) => {
    setCaptchaText(text);
  };
  
  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          <PersonalInfoFields form={form} />
          <BusinessInfoFields form={form} />
          <PasswordFields form={form} />
          
          <FormField
            control={form.control}
            name="captcha"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Security Check</FormLabel>
                <div className="space-y-2">
                  <SimpleCaptcha onGenerate={handleCaptchaGenerate} />
                  <FormControl>
                    <Input 
                      {...field} 
                      placeholder="Enter the text shown above"
                      autoComplete="off" 
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <SignupFormFooter form={form} isLoading={isLoading} />
        </form>
      </Form>
      
      <AuthFooter 
        text="Already have an account?"
        linkText="Log in"
        linkTo="/login"
      />
    </div>
  );
};

export default SaasSignupFormContainer;
